import { getScrollTop, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * When the header turns to glass.
 *
 * Horizon already ships a scrolled flag — `data-sticky-state` — but it flips as
 * soon as the header leaves the top of the viewport, roughly one pixel in. The
 * design keeps the header clear for as long as the hero is the thing being
 * looked at, so the switch needs a threshold of its own, and the styling in
 * brand.css hangs off `data-kk-glass` rather than off the sticky state.
 *
 * Over a hero: 55% of the viewport, but never less than 380px, so a short
 * window still gets a deliberate stretch of transparency rather than a header
 * that frosts over almost immediately.
 *
 * Everywhere else there is nothing to stay clear of, so the glass arrives on
 * the first real scroll; 12px only keeps it off while the page is at rest.
 */
const HERO_THRESHOLD_RATIO = 0.55;
const HERO_THRESHOLD_MINIMUM = 380;
const FLAT_THRESHOLD = 12;

const header = document.getElementById('header-component');

if (header) {
  let scrollTarget = getScrollEventTarget();
  let frame = null;

  const threshold = () =>
    header.hasAttribute('transparent')
      ? Math.max(HERO_THRESHOLD_RATIO * window.innerHeight, HERO_THRESHOLD_MINIMUM)
      : FLAT_THRESHOLD;

  const update = () => {
    frame = null;
    header.dataset.kkGlass = getScrollTop() > threshold() ? 'on' : 'off';
  };

  const onScroll = () => {
    if (frame !== null) return;
    frame = requestAnimationFrame(update);
  };

  /**
   * The scroll container swaps between `.page-wrapper` and the document at
   * 990px, so the listener has to move with it — the same rebinding Horizon's
   * own header does on this breakpoint.
   */
  const rebind = () => {
    scrollTarget.removeEventListener('scroll', onScroll);
    scrollTarget = getScrollEventTarget();
    scrollTarget.addEventListener('scroll', onScroll, { passive: true });
    update();
  };

  scrollTarget.addEventListener('scroll', onScroll, { passive: true });
  scrollContainerMediaQuery.addEventListener('change', rebind);

  // The threshold is a fraction of the viewport, so it moves when the viewport does.
  window.addEventListener('resize', onScroll, { passive: true });

  update();
}
